import { type Extensions, Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import { Details, DetailsContent, DetailsSummary } from "@tiptap/extension-details";
import { TableKit } from "@editor/components/tiptap-node/table-node/extensions/table-node-extension";
import { TableHandleExtension } from "@editor/components/tiptap-node/table-node/extensions/table-handle";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { NodeBackground } from "@editor/components/tiptap-extension/node-background-extension";
import UniqueID from "@tiptap/extension-unique-id";
import { Plugin } from "prosemirror-state";
import { CitationHighlightMark } from "./citation-highlight-mark";

// Custom extension to handle paste rules for cleaning up pasted content
const CleanPasteExtension = Extension.create({
    name: "cleanPaste",
    addProseMirrorPlugins() {
        return [
            new Plugin({
                // The TextStyleKit extension will intercept paste events so we use appendTransaction to clean up after the paste is done
                appendTransaction(transactions, oldState, newState) {
                    let tr = newState.tr;
                    let modified = false;
                    // Remove backgroundColor from all nodes
                    newState.doc.descendants((node, pos) => {
                        if (node.attrs && node.attrs["backgroundColor"]) {
                            const newAttrs = { ...node.attrs };
                            delete newAttrs["backgroundColor"];
                            tr = tr.setNodeMarkup(pos, undefined, newAttrs);
                            modified = true;
                        }
                    });
                    // Remove textStyle and highlight marks from the whole doc because we don't support them right now
                    const { textStyle, highlight } = newState.schema.marks;
                    if (textStyle || highlight) {
                        const docSize = newState.doc.content.size;
                        if (textStyle) {
                            tr = tr.removeMark(0, docSize, textStyle);
                            modified = true;
                        }
                        if (highlight) {
                            tr = tr.removeMark(0, docSize, highlight);
                            modified = true;
                        }
                    }
                    if (modified) {
                        return tr;
                    }
                    return null;
                },
            }),
        ];
    },
});

const baseExtensions = [
    StarterKit.configure({
        dropcursor: {
            width: 2,
        },
    }),
    Image.configure({
        allowBase64: true,
        resize: {
            enabled: true,
            directions: ["top-left", "top-right", "bottom-left", "bottom-right"],
            minWidth: 50,
            minHeight: 50,
            alwaysPreserveAspectRatio: true,
        },
    }),
    Highlight.configure({ multicolor: true }),
    NodeBackground,
    TextStyleKit,
    TextAlign.configure({
        types: ["heading", "paragraph"],
    }),
    TableKit.configure({
        table: {
            resizable: true,
            cellMinWidth: 70,
        },
    }),
    TableHandleExtension,
    UniqueID.configure({
        types: ["table", "paragraph", "bulletList", "orderedList", "taskList", "heading", "blockquote", "codeBlock"],
    }),
    CleanPasteExtension,
];

const previewExtensions = [
    StarterKit,
    Image,
    TextAlign.configure({
        types: ["heading", "paragraph"],
    }),
    TableKit,
    CitationHighlightMark,
    Details.configure({
        persist: true,
        HTMLAttributes: {
            class: "details",
        },
        // The node view renders the toggle as a sibling of <summary>, so the label has to
        // live inside the button for the whole row to be a hit target. Called on mount,
        // on every toggle, and on node update, so it stays in sync with the summary node.
        renderToggleButton: ({ element, isOpen, node }) => {
            element.setAttribute("aria-label", isOpen ? "Hide quoted replies" : "Show quoted replies");
            element.textContent = node.firstChild?.textContent ?? "";
        },
    }),
    DetailsContent,
    DetailsSummary,
];

const GetEditorExtensions = ({
    preview = false,
    placeholder,
    extensionOverrides = [],
}: {
    preview?: boolean;
    placeholder: string;
    extensionOverrides?: Extensions;
}) =>
    preview
        ? previewExtensions
        : [
              ...baseExtensions,
              Placeholder.configure({
                  placeholder,
                  emptyNodeClass: "is-empty with-slash",
              }),
              ...extensionOverrides,
          ];

const reminderExtensions = [
    ...baseExtensions,
    Placeholder.configure({
        placeholder: "Type your reminder message here...",
    }),
];

export { baseExtensions, previewExtensions, reminderExtensions, GetEditorExtensions };
